/**
 * SILVERBACK MOTO - Menu Controller
 * Handles desktop mega menu and mobile menu interactions
 */

(function() {
  'use strict';

  /* ============================================
     DESKTOP MEGA MENU
     ============================================ */
  
  class MegaMenuController {
    constructor() {
      this.triggers = document.querySelectorAll('[data-mega-menu-trigger]');
      this.menus = document.querySelectorAll('[data-mega-menu]');
      this.closeButtons = document.querySelectorAll('[data-mega-menu-close]');
      this.activeMenu = null;
      this.hoverTimeout = null;
      this.closeTimeout = null;
      
      if (this.triggers.length > 0) {
        this.init();
      }
    }

    init() {
      this.bindTriggers();
      this.bindCloseButtons();
      this.bindCategoryHovers();
      this.bindKeyboard();
      this.bindOutsideClick();
    }

    bindTriggers() {
      this.triggers.forEach(trigger => {
        const menuId = trigger.dataset.megaMenuTrigger;
        
        // Mouse enter - open menu
        trigger.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimeout);
          this.hoverTimeout = setTimeout(() => {
            this.open(menuId);
          }, 80);
        });
        
        // Mouse leave - start close timer
        trigger.addEventListener('mouseleave', () => {
          clearTimeout(this.hoverTimeout);
          this.startCloseTimer();
        });
        
        // Click - toggle menu
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle(menuId);
        });
      });
      
      // Keep menu open when hovering over it
      this.menus.forEach(menu => {
        menu.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimeout);
        });
        
        menu.addEventListener('mouseleave', () => {
          this.startCloseTimer();
        });
      });
    }

    bindCloseButtons() {
      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });
    }

    bindCategoryHovers() {
      const categoryTriggers = document.querySelectorAll('[data-category-trigger]');
      
      categoryTriggers.forEach(trigger => {
        trigger.addEventListener('mouseenter', () => {
          const categoryHandle = trigger.dataset.categoryTrigger;
          const menu = trigger.closest('[data-mega-menu]');
          this.switchCategory(menu, categoryHandle);
        });
        
        // Also handle click for touch devices
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const categoryHandle = trigger.dataset.categoryTrigger;
          const menu = trigger.closest('[data-mega-menu]');
          this.switchCategory(menu, categoryHandle);
        });
      });
    }

    bindKeyboard() {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeMenu) {
          this.close();
        }
      });
    }

    bindOutsideClick() {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-mega-menu]') && 
            !e.target.closest('[data-mega-menu-trigger]')) {
          this.close();
        }
      });
    }

    switchCategory(menu, categoryHandle) {
      // Update category buttons
      const categoryBtns = menu.querySelectorAll('[data-category-trigger]');
      categoryBtns.forEach(btn => {
        btn.classList.remove('mega-menu__category--active');
        if (btn.dataset.categoryTrigger === categoryHandle) {
          btn.classList.add('mega-menu__category--active');
        }
      });
      
      // Update product lists
      const productLists = menu.querySelectorAll('[data-category-products]');
      productLists.forEach(list => {
        list.classList.remove('mega-menu__products--active');
        if (list.dataset.categoryProducts === categoryHandle) {
          list.classList.add('mega-menu__products--active');
        }
      });
    }

    open(menuId) {
      this.close();
      
      const menu = document.querySelector(`[data-mega-menu="${menuId}"]`);
      const trigger = document.querySelector(`[data-mega-menu-trigger="${menuId}"]`);
      
      if (menu) {
        menu.classList.add('mega-menu--open');
        menu.setAttribute('aria-hidden', 'false');
        trigger?.closest('.nav__item')?.classList.add('nav__item--open');
        this.activeMenu = menu;
      }
    }

    close() {
      this.menus.forEach(menu => {
        menu.classList.remove('mega-menu--open');
        menu.setAttribute('aria-hidden', 'true');
      });
      
      document.querySelectorAll('.nav__item--open').forEach(item => {
        item.classList.remove('nav__item--open');
      });
      
      this.activeMenu = null;
    }

    toggle(menuId) {
      if (this.activeMenu?.dataset.megaMenu === menuId) {
        this.close();
      } else {
        this.open(menuId);
      }
    }

    startCloseTimer() {
      this.closeTimeout = setTimeout(() => {
        this.close();
      }, 200);
    }
  }

  /* ============================================
     MOBILE MENU
     ============================================ */
  
  class MobileMenuController {
    constructor() {
      this.menu = document.querySelector('[data-mobile-menu]');
      this.openTriggers = document.querySelectorAll('[data-mobile-menu-open]');
      this.closeTriggers = document.querySelectorAll('[data-mobile-menu-close]');
      this.navTriggers = document.querySelectorAll('[data-mobile-nav-trigger]');
      this.contents = document.querySelectorAll('[data-mobile-content]');
      
      if (this.menu) {
        this.init();
      }
    }

    init() {
      // Open menu
      this.openTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => this.open());
      });
      
      // Close menu
      this.closeTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => this.close());
      });
      
      // Navigation tabs
      this.navTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const target = trigger.dataset.mobileNavTrigger;
          this.switchContent(target);
          
          // Update active state
          this.navTriggers.forEach(t => t.classList.remove('mobile-menu__nav-item--active'));
          trigger.classList.add('mobile-menu__nav-item--active');
        });
      });
      
      // Category accordions — delegated click on the menu container.
      // Robust against script-order/timing issues and survives any re-render.
      // Toggles `--collapsed` (the class with matching CSS) and syncs aria-expanded.
      this.menu.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-mobile-category-toggle]');
        if (!toggle) return;
        e.preventDefault();
        e.stopPropagation();
        const category = toggle.closest('[data-mobile-category]');
        if (!category) return;
        const collapsed = category.classList.toggle('mobile-menu__category--collapsed');
        toggle.setAttribute('aria-expanded', String(!collapsed));
      });
      
      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    open() {
      this.menu.classList.add('mobile-menu--open');
      this.menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.menu.classList.remove('mobile-menu--open');
      this.menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    isOpen() {
      return this.menu.classList.contains('mobile-menu--open');
    }

    switchContent(target) {
      this.contents.forEach(content => {
        content.classList.remove('mobile-menu__content--active');
        if (content.dataset.mobileContent === target) {
          content.classList.add('mobile-menu__content--active');
        }
      });
    }
  }

  /* ============================================
     INITIALIZE
     ============================================ */
  
  function init() {
    new MegaMenuController();
    new MobileMenuController();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load
  document.addEventListener('shopify:section:load', init);
})();